import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Transition } from 'react-transition-group';
import { useNavigate } from 'react-router-dom';

import { ProfileDropup } from '../ProfileDropup';
import { OrganizationDropdown } from '../OrganizationDropdown';
import { NuxModal } from '../NuxModal';
import { Icon } from '../../atoms';
import { SidebarToggleButton, Button, Tooltip } from '../../molecules';
import { useDetectScrolledToBottom, useDropShadow } from '../../../hooks';
import { usePortal } from '../../../providers';

import { NUX_MODAL_SEEN } from '../../../constants';
import { userShape } from '../../../propsShape';

const SidebarContainer = styled.aside`
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  background: var(--elevation-low);
  flex-shrink: 0;
  height: 100%;
  border-right: 1px solid var(--border-muted);

  width: ${({ $state }) => ($state === 'entering' || $state === 'entered' ? '240px' : '0')};

  transform: ${({ $state }) => {
    if ($state === 'entering' || $state === 'entered') {
      return 'translate3d(0, 0, 0)';
    }
    return 'translate3d(-240px, 0, 0)';
  }};

  transition: width 250ms cubic-bezier(0.4, 0, 0.2, 1), transform 250ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const StyledHeader = styled.header`
  display: flex;
  padding: 22px 16px;
  border-bottom: 1px solid var(--border-muted);
  justify-content: space-between;
`;

const SidebarMain = styled.main`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  padding: 16px;
  gap: 16px;

  max-height: calc(
    100vh - 76px - 77px
  ); // 76px is the height of the footer, 77px is the height of the header with bottom border

  overflow-y: scroll;
  scrollbar-color: var(--gray-40) transparent;
  scrollbar-width: thin;
`;

const SidebarFooter = styled.footer`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  padding: 20px 16px;
  margin-top: auto;

  transition: box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1),
    border-top 200ms cubic-bezier(0.4, 0, 0.2, 1);

  box-shadow: ${({ $dropShadow }) => ($dropShadow ? '0px -16px 32px var(--gray-8)' : 'none')};
  border-top: ${({ $dropShadow }) =>
    $dropShadow ? '1px solid var(--border-muted)' : '1px solid transparent'};
`;

const SidebarFooterActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 239px;
  box-sizing: border-box;
  align-items: center;
`;

const HowToUseSupperIcon = styled(Icon)`
  cursor: pointer;
  border-radius: 20px;
  &:hover:not(:active) {
    background-color: var(--wash-gray-hover);
  }
`;

const Sidebar = ({
  className = null,
  collapsed = false,
  collapseSidebar,
  children = null,
  organizations,
  currentOrganization,
  selectOrganization,
  user,
  onLogoutClick,
  onNavigationScroll,
  isReadOnly = false,
  isAgentic = false
}) => {
  // This state is used to force a remount of the ProfileDropup when the sidebar expands.
  const [sidebarVersion, setSidebarVersion] = useState(0);
  const mainRef = useRef(null);
  const navigate = useNavigate();
  const { dropShadow } = useDropShadow(mainRef);
  const { showPortal } = usePortal();
  useDetectScrolledToBottom(mainRef, onNavigationScroll);

  const isAdmin = useMemo(
    () =>
      user?.memberships
        .find((membership) => membership.organizationId === currentOrganization.id)
        ?.entitlements.some((entitlement) => entitlement.role.name.toLowerCase() === 'admin'),
    [user, currentOrganization.id]
  );

  const handleHelpClick = useCallback(() => {
    showPortal(<NuxModal onComplete={() => navigate('/chats/new')} isReadOnly={isReadOnly} />);
  }, [showPortal]);

  useEffect(() => {
    if (!localStorage.getItem(NUX_MODAL_SEEN)) {
      handleHelpClick();
      localStorage.setItem(NUX_MODAL_SEEN, new Date().toISOString());
    }
  }, []);

  const newChatUrl = isAgentic ? '/agentic-chats/new' : '/chats/new';
  const buttonLabel = isAgentic ? 'Start new agentic chat' : 'Start new chat';

  return (
    <Transition
      in={!collapsed}
      timeout={250}
      unmountOnExit
      mountOnEnter
      // When the sidebar expansion finishes, increment sidebarVersion and trigger a window resize.
      onEntered={() => {
        setSidebarVersion((prev) => prev + 1);
        window.dispatchEvent(new Event('resize'));
      }}
      // When the sidebar collapse finishes, increment sidebarVersion and trigger a window resize.
      onExited={() => {
        setSidebarVersion((prev) => prev + 1);
        window.dispatchEvent(new Event('resize'));
      }}
    >
      {(state) => (
        <SidebarContainer className={className} $state={state}>
          <StyledHeader>
            <OrganizationDropdown
              key={`organization-${sidebarVersion}`}
              currentOrganization={currentOrganization}
              organizations={organizations}
              onSelect={selectOrganization}
            />
            <SidebarToggleButton collapsed={collapsed} onClick={collapseSidebar} />
          </StyledHeader>

          <SidebarMain ref={mainRef}>
            {!isReadOnly && !isAgentic && (
              <Button secondary block onClick={() => navigate(newChatUrl)}>
                {buttonLabel}
              </Button>
            )}
            {children}
          </SidebarMain>
          <SidebarFooter $dropShadow={dropShadow}>
            <SidebarFooterActionsContainer>
              <ProfileDropup
                key={`profile-${sidebarVersion}`}
                picture={user?.picture}
                name={user?.name}
                onLogoutClick={onLogoutClick}
                isAdmin={isAdmin}
              />
              <Tooltip content='How to use Supper'>
                <HowToUseSupperIcon
                  name='help-circle'
                  onClick={handleHelpClick}
                  color='var(--content-subtitle)'
                />
              </Tooltip>
            </SidebarFooterActionsContainer>
          </SidebarFooter>
        </SidebarContainer>
      )}
    </Transition>
  );
};

const organizationProps = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string
});

Sidebar.propTypes = {
  className: PropTypes.string,
  collapsed: PropTypes.bool,
  collapseSidebar: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  user: userShape.isRequired,
  currentOrganization: organizationProps.isRequired,
  organizations: PropTypes.arrayOf(organizationProps).isRequired,
  selectOrganization: PropTypes.func.isRequired,
  onLogoutClick: PropTypes.func.isRequired,
  onNavigationScroll: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  isAgentic: PropTypes.bool
};

export { Sidebar };
